// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { openInvoice } from '@/billing/api/open-invoice';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UseOpenInvoiceOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (message: string, apiStatusCode: number) => void;
}

export function useOpenInvoice(options: UseOpenInvoiceOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => openInvoice(options.invoiceKey),
    onSuccess: async () => {
      await invalidateInvoiceQueries({
        queryClient,
        invoiceKey: options.invoiceKey,
      });
      options.onSuccess?.();
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode
      );
    },
  });
}
